// [S-061b] Precedents data hook. Backed by the admin REST API
// `POST /precedents/search` which today wraps a stub that always returns
// `l2Deployed: false` — the screen branches on that flag to render the
// "Schedule Layer 2 setup" empty state per Brief §2.5.
//
// The wire shape matches PrecedentSearchResult in @csai/core; kept inline
// so the web bundle doesn't pull in the core barrel for type-only imports.
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { useApi } from "./api";

// [S-636] Search returns the SAME rich rows as browse (PrecedentListItem), so
// the UI renders one card in both modes.
export type PrecedentSearchResponse = {
  precedents: PrecedentListItem[];
  l2Deployed: boolean;
};

export type PrecedentSearchInput = {
  queryText: string;
  dealType?: string;
  talentTier?: string;
  territory?: string;
  term?: string;
  compensationBand?: string;
  guild?: string;
  topK?: number;
  /** [S-636] Explicit party-name filter (else the query doubles as one). */
  party?: string;
  /** platform_admin scopes to a tenant; ignored for tenant users. */
  tenantId?: string;
};

// [S-285] Browse + open-file wire shapes (match @csai/core).
// [S-277] Visibility tier for the badge + exclude/re-share control.
export type PrecedentVisibility = "tenant" | "business_affairs" | "private";

export type PrecedentListItem = {
  documentId: string;
  dealType: string | null;
  guild: string | null;
  tier: string | null;
  uploadedAt: string;
  clauseCount: number;
  summary: string;
  parties: string[];
  baseAmount: number | null;
  currency: string | null;
  dimensions: Record<string, string>;
  visibility: PrecedentVisibility;
  ownerUid: string;
};

export type PrecedentListResponse = {
  precedents: PrecedentListItem[];
  /** Total across ALL pages — what sizes the pager. Optional so an older API
   *  (or a fixture) that omits it degrades to "what came back". */
  total?: number;
  l2Deployed: boolean;
};

export type PrecedentClause = {
  id: string;
  type: string;
  text: string;
  // [Phase 2] Type-specific structured terms for this clause.
  attributes: Record<string, string>;
};

export type PrecedentDetail = {
  documentId: string;
  tenantId: string;
  lifecycleState: string;
  uploadedAt: string;
  dealType: string | null;
  guild: string | null;
  tier: string | null;
  compensation: {
    baseAmount: number | null;
    currency: string | null;
    paymentSchedule: string | null;
  };
  clauses: PrecedentClause[];
  // Party / talent / counterparty names extracted from the contract.
  parties: string[];
  // [Phase 1] Flexible comparability dimensions (instrumentType, role, …).
  dimensions: Record<string, string>;
  // [S-286] When true, the original source document is stored and openable
  // at GET /precedents/:id/source.
  hasSource: boolean;
  sourceContentType: string | null;
  visibility: PrecedentVisibility;
  ownerUid: string;
};

// [S-282] Tag vocabulary for the search autocomplete — the dealType/guild
// values that actually exist in the tenant's indexed corpus, with counts.
export type PrecedentFacet = { value: string; count: number };
export type PrecedentFacetsResponse = {
  dealType: PrecedentFacet[];
  guild: PrecedentFacet[];
  l2Deployed: boolean;
};

export const PRECEDENTS_KEY = ["precedents"] as const;

export function useSearchPrecedents(): UseMutationResult<
  PrecedentSearchResponse,
  Error,
  PrecedentSearchInput
> {
  const api = useApi("admin");
  return useMutation({
    mutationFn: (input) =>
      api<PrecedentSearchResponse>("/precedents/search", {
        method: "POST",
        body: input,
      }),
  });
}

function qs(params: Record<string, string | number | undefined>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && String(v).length > 0) sp.set(k, String(v));
  }
  const s = sp.toString();
  return s.length > 0 ? `?${s}` : "";
}

// [S-285] Browse all indexed precedents for a tenant. `enabled` lets the
// screen hold the query until a tenant is in scope (platform_admin picker).
export function useListPrecedents(
  opts: {
    tenantId?: string;
    dealType?: string;
    guild?: string;
    party?: string;
    tier?: string;
    amountMin?: number;
    amountMax?: number;
    dateFrom?: string;
    dateTo?: string;
    limit?: number;
    offset?: number;
  } = {},
  enabled = true
): UseQueryResult<PrecedentListResponse, Error> {
  const api = useApi("admin");
  return useQuery({
    queryKey: [...PRECEDENTS_KEY, "list", opts] as const,
    enabled,
    // Keep the prior page visible while a filter change refetches, so the list
    // never flashes to the "ingest your deals" empty state mid-fetch.
    placeholderData: keepPreviousData,
    queryFn: () =>
      api<PrecedentListResponse>(
        `/precedents${qs({
          tenantId: opts.tenantId,
          dealType: opts.dealType,
          guild: opts.guild,
          party: opts.party,
          tier: opts.tier,
          amountMin: opts.amountMin,
          amountMax: opts.amountMax,
          dateFrom: opts.dateFrom,
          dateTo: opts.dateTo,
          limit: opts.limit,
          offset: opts.offset,
        })}`
      ),
  });
}

// [S-281] Remove (de-index) a precedent. Drops the vector + structured record
// server-side (the source file in GCS is retained). Invalidates the precedent
// list/detail caches so the browse view drops the row immediately.
export function useRemovePrecedent(): UseMutationResult<
  void,
  Error,
  { id: string; tenantId?: string }
> {
  const api = useApi("admin");
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, tenantId }) =>
      api<void>(
        `/precedents/${encodeURIComponent(id)}${qs({ tenantId })}`,
        { method: "DELETE" }
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PRECEDENTS_KEY });
    },
  });
}

// [S-277] Exclude from shared (→ private) / re-share (→ tenant) a precedent.
// Owner-or-tenant_admin only (enforced server-side). Forward-only. Invalidates
// the precedent caches so the tier badge + browse membership update everywhere.
export function useSetPrecedentVisibility(): UseMutationResult<
  { id: string; visibility: PrecedentVisibility },
  Error,
  { id: string; visibility: PrecedentVisibility; tenantId?: string }
> {
  const api = useApi("admin");
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, visibility, tenantId }) =>
      api<{ id: string; visibility: PrecedentVisibility }>(
        `/precedents/${encodeURIComponent(id)}/visibility${qs({ tenantId })}`,
        { method: "POST", body: { visibility } }
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PRECEDENTS_KEY });
    },
  });
}

// [S-282] Fetch the tenant's taxonomy tags for the search autocomplete.
// `enabled` holds until a tenant is in scope (platform_admin picker).
export function usePrecedentFacets(
  tenantId?: string,
  enabled = true
): UseQueryResult<PrecedentFacetsResponse, Error> {
  const api = useApi("admin");
  return useQuery({
    queryKey: [...PRECEDENTS_KEY, "facets", tenantId] as const,
    enabled,
    queryFn: () =>
      api<PrecedentFacetsResponse>(`/precedents/facets${qs({ tenantId })}`),
  });
}

// [Phase 3] The studio's vocabulary inferred from its corpus.
export type StudioVocabulary = {
  instrumentType: PrecedentFacet[];
  counterpartyRole: PrecedentFacet[];
  clauseType: PrecedentFacet[];
  dealType: PrecedentFacet[];
  guild: PrecedentFacet[];
  tier: PrecedentFacet[];
  budgetBand: PrecedentFacet[];
  precedentCount: number;
  l2Deployed: boolean;
};

export function useStudioVocabulary(
  tenantId?: string,
  enabled = true
): UseQueryResult<StudioVocabulary, Error> {
  const api = useApi("admin");
  return useQuery({
    queryKey: [...PRECEDENTS_KEY, "vocabulary", tenantId] as const,
    enabled,
    queryFn: () =>
      api<StudioVocabulary>(`/precedents/vocabulary${qs({ tenantId })}`),
  });
}

// [Phase 5] Negotiation reasonableness — comparable clauses of one type across
// the studio's L2 corpus, ranked by situation similarity (tier × budget ×
// recency dominate). Backs the "Compare across our deals" drawer. Wire shapes
// mirror ComparableClause / ComparableClausesResult in @csai/core.
export type ComparableClause = {
  documentId: string;
  score: number;
  clause: PrecedentClause;
  dealType: string | null;
  guild: string | null;
  tier: string | null;
  instrumentType: string | null;
  counterpartyRole: string | null;
  budgetBand: string | null;
  parties: string[];
};

export type ComparableClausesResult = {
  clauseType: string;
  results: ComparableClause[];
  l2Deployed: boolean;
};

export type CompareClausesInput = {
  clauseType: string;
  tenantId?: string;
  dealType?: string;
  guild?: string;
  tier?: string;
  budgetBand?: string;
  counterpartyRole?: string;
  instrumentType?: string;
  limit?: number;
};

// Enabled only once the caller has a clauseType (the one required dimension).
// `enabled` lets the drawer hold the query until the reviewer submits the
// situation form, and the platform_admin tenant scope is in place.
export function useCompareClauses(
  input: CompareClausesInput | null,
  enabled = true
): UseQueryResult<ComparableClausesResult, Error> {
  const api = useApi("admin");
  return useQuery({
    queryKey: [...PRECEDENTS_KEY, "compare", input] as const,
    enabled: enabled && input !== null && input.clauseType.length > 0,
    queryFn: () =>
      api<ComparableClausesResult>(
        `/precedents/compare${qs({
          clauseType: input!.clauseType,
          tenantId: input!.tenantId,
          dealType: input!.dealType,
          guild: input!.guild,
          tier: input!.tier,
          budgetBand: input!.budgetBand,
          counterpartyRole: input!.counterpartyRole,
          instrumentType: input!.instrumentType,
          limit: input!.limit,
        })}`
      ),
  });
}

// [S-285] Open one precedent (full clause content) for the detail view.
export function useGetPrecedent(
  id: string | undefined,
  tenantId?: string
): UseQueryResult<PrecedentDetail, Error> {
  const api = useApi("admin");
  return useQuery({
    queryKey: [...PRECEDENTS_KEY, "detail", id, tenantId] as const,
    enabled: id !== undefined && id.length > 0,
    queryFn: () =>
      api<PrecedentDetail>(
        `/precedents/${encodeURIComponent(id ?? "")}${qs({ tenantId })}`
      ),
  });
}

// [S-643 follow-up] Reclassify documentForm (deal-memo vs. long-form) on
// already-ingested precedents from their stored clause text. Async +
// polled — mirrors precedentAnalyticsQuery.ts's BackfillRun/
// useStartBackfill/useBackfillStatus pattern (S-617) exactly. Found live:
// the original synchronous version gave an admin running it against a
// 157-precedent corpus no way to tell it apart from a hang, and no way to
// stop it — a real progress bar (backed by a persisted run record the admin
// can poll or revisit) fixes both.
export type DocumentFormBackfillRunStatus =
  | "running"
  | "completed"
  | "failed"
  | "cancelled";

export type DocumentFormBackfillRun = {
  runId: string;
  status: DocumentFormBackfillRunStatus;
  startedBy: string;
  total: number;
  processed: number;
  classified: number;
  unclassifiable: number;
  skippedAlreadyClassified: number;
  skippedNoClauseRecords: number;
  failed: number;
  startedAt: string;
  completedAt?: string;
  error?: string;
};

export function useStartDocumentFormBackfill(
  tenantId: string
): UseMutationResult<DocumentFormBackfillRun, Error, { force?: boolean }> {
  const api = useApi("admin");
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ force }) =>
      api<DocumentFormBackfillRun>(
        `/precedents/backfill-document-form${qs({ tenantId })}`,
        { method: "POST", body: { force } }
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["document-form-backfill-status", tenantId],
      });
    },
  });
}

/** Polls every 2s while the latest run is 'running'; stops once it settles. */
export function useDocumentFormBackfillStatus(
  tenantId: string,
  enabled: boolean
): UseQueryResult<DocumentFormBackfillRun | null, Error> {
  const api = useApi("admin");
  return useQuery({
    queryKey: ["document-form-backfill-status", tenantId],
    queryFn: () =>
      api<DocumentFormBackfillRun | null>(
        `/precedents/backfill-document-form-status${qs({ tenantId })}`
      ),
    enabled,
    refetchInterval: (query) =>
      query.state.data?.status === "running" ? 2000 : false,
    // A completed/failed run's onSuccess invalidation and a manual refetch
    // both need fresh data every time, not a cached snapshot from a prior
    // poll — same reasoning as the precedent-analytics status query.
    staleTime: 0,
  });
}

// [S-643 follow-up] Stop the tenant's currently-running reclassify scan —
// found live: no way to cancel a run against a large corpus. A no-op
// server-side if nothing is running. Doesn't invalidate the status query
// itself — the existing 2s poll (while status stays "running") picks up
// the eventual "cancelled" state on its own; stopping isn't instant.
export function useCancelDocumentFormBackfill(): UseMutationResult<
  DocumentFormBackfillRun | null,
  Error,
  { tenantId?: string }
> {
  const api = useApi("admin");
  return useMutation({
    mutationFn: ({ tenantId }) =>
      api<DocumentFormBackfillRun | null>(
        `/precedents/backfill-document-form-cancel${qs({ tenantId })}`,
        { method: "POST" }
      ),
  });
}
